import {
  HUMAN_TAKEOVER_START_TOKEN,
  HUMAN_TAKEOVER_END_TOKEN,
  CONTEXT_COMPACTED_TOKEN,
} from "./conversationConstants";

// Chat statuses where the agent is actively working and the composer must stay locked. Keep this
// the single source of truth — both the composer's `isThinking` gate and the in-thread thinking
// indicator (`needsThinking`) derive from it, so a new busy state can never drift between them.
const AGENT_BUSY_STATUSES = new Set(["submitted", "streaming", "cancelling"]);

export function isAgentBusyStatus(chatStatus) {
  return AGENT_BUSY_STATUSES.has(chatStatus);
}

export function buildStatusConfig(p) {
  return {
    COMPLETED:   { ...p.green, label: "Completed" },
    ERROR:       { ...p.red, label: "Error" },
    MAX_ITERATIONS_REACHED: { ...p.amber, label: "Max iterations" },
    IN_PROGRESS: { ...p.blue, label: "In progress" },
    CANCELLED:   { ...p.zinc, label: "Cancelled" },
    WAITING_FOR_SCHEDULED_MESSAGE: { ...p.purple, label: "Scheduled" },
    WAITING_FOR_FORM_SUBMISSION: { ...p.purple, label: "Waiting for form" },
    WAITING_FOR_REVIEW:    { ...p.amberAlt, label: "Under review" },
    SESSION_UNDER_REVIEW:  { ...p.amberAlt, label: "Under review" },
    HUMAN_TAKEOVER:        { ...p.teal, label: "Human takeover" },
    WAITING_FOR_WORKERS:   { ...p.purple, label: "Waiting for workers" },
  };
}

export function buildResponseReqConfig(p) {
  return {
    REQUIRED:     { ...p.red, label: "Response required" },
    NOT_REQUIRED: { ...p.zinc, label: "No response needed" },
  };
}

export function buildSupervisorDecisionConfig(p) {
  return {
    APPROVE:  { ...p.greenAlt, label: "Approve" },
    REVISE:   { ...p.amberAlt, label: "Revise" },
    TAKEOVER: { ...p.redAlt, label: "Takeover" },
  };
}

export function formatAbsoluteTime(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatMessageTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) {
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  }
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatCountdown(targetDate) {
  if (!targetDate) return null;
  const diff = new Date(targetDate) - new Date();
  if (diff <= 0) return null;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

export function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
}

export function parseHumanTakeoverMarker(content) {
  if (typeof content !== "string" || !content.trim()) return null;
  if (content.includes(HUMAN_TAKEOVER_START_TOKEN)) {
    const message =
      content.replace(HUMAN_TAKEOVER_START_TOKEN, "").trim() ||
      "Human takeover started. AI replies are paused.";
    return { kind: "start", message };
  }
  if (content.includes(HUMAN_TAKEOVER_END_TOKEN)) {
    const message =
      content.replace(HUMAN_TAKEOVER_END_TOKEN, "").trim() ||
      "Human takeover ended. AI can resume on the next message.";
    return { kind: "end", message };
  }
  return null;
}

export function parseContextCompactedMarker(content) {
  if (typeof content !== "string" || !content.trim()) return null;
  if (!content.includes(CONTEXT_COMPACTED_TOKEN)) return null;
  const message =
    content.replace(CONTEXT_COMPACTED_TOKEN, "").trim() ||
    "Older conversation history has been summarized to maintain performance.";
  return { message };
}

export function isDynamicFormTool(toolName) {
  return (
    toolName === "form_fields" ||
    toolName === "get_form" ||
    toolName === "fill_form"
  );
}

export function isCreatedFileTool(toolName) {
  return (
    toolName === "create_markdown_file" ||
    toolName === "create_text_file" ||
    toolName === "create_json_file" ||
    toolName === "create_csv_file"
  );
}

export function inferMimeTypeFromFileName(fileName) {
  if (typeof fileName !== "string") return "";
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".md")) return "text/markdown";
  if (lower.endsWith(".txt")) return "text/plain";
  if (lower.endsWith(".json")) return "application/json";
  if (lower.endsWith(".csv")) return "text/csv";
  return "";
}

export function buildDynamicFormContext(args, result) {
  const parsedArgs = parseMaybeJson(args);
  const parsedResult = parseMaybeJson(result);
  const formId =
    parsedResult?.formInstanceId ||
    parsedResult?.form?.formInstanceId ||
    parsedArgs?.formInstanceId ||
    parsedArgs?.formInstance?.formInstanceId ||
    null;
  const title =
    parsedResult?.form?.title ||
    parsedArgs?.formInstance?.title ||
    parsedArgs?.title ||
    "Dynamic Form";
  const progressStatus =
    parsedResult?.form?.progressStatus ||
    parsedArgs?.formInstance?.progressStatus ||
    null;
  return {
    formId,
    args,
    result,
    title,
    progressStatus,
  };
}

export function buildCreatedFileContext(args, result, toolName = null) {
  const parsedArgs = parseMaybeJson(args);
  const parsedResult = parseMaybeJson(result);
  const fileId = parsedResult?.fileId || parsedResult?.id || parsedArgs?.fileId || null;
  const fileName = parsedResult?.fileName || parsedArgs?.fileName || "Untitled file";
  const mimeType =
    parsedResult?.mimeType ||
    parsedArgs?.mimeType ||
    inferMimeTypeFromFileName(fileName);
  const downloadUrl = parsedResult?.downloadUrl || null;
  const size = parsedResult?.size ?? parsedResult?.fileSize ?? null;
  const createdAt = parsedResult?.createdAt || parsedResult?.createDate || null;
  const agentId = parsedResult?.agentId || parsedArgs?.agentId || null;
  const sourceTool = parsedResult?.sourceTool || toolName;
  const toolError = parsedResult?.error || null;
  return {
    fileId,
    fileName,
    mimeType,
    downloadUrl,
    size,
    createdAt,
    agentId,
    sourceTool,
    toolError,
    args,
    result,
  };
}

export function getLatestDynamicFormEvent(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return null;
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg?.role !== "assistant") continue;
    const parts = msg.parts || [];
    for (let p = parts.length - 1; p >= 0; p--) {
      const part = parts[p];
      if (part.type !== "tool" || !isDynamicFormTool(part.toolName)) continue;
      const argsStr =
        part.input !== undefined && part.input !== null
          ? typeof part.input === "string" ? part.input : JSON.stringify(part.input)
          : part.inputText || null;
      const resultStr =
        part.output !== undefined && part.output !== null
          ? typeof part.output === "string" ? part.output : JSON.stringify(part.output)
          : null;
      const context = buildDynamicFormContext(argsStr, resultStr);
      return {
        key: `${i}:${p}:${context.formId || "pending"}`,
        ...context,
      };
    }
  }
  return null;
}

export function getLatestCreatedFileEvent(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return null;
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg?.role !== "assistant") continue;
    const parts = msg.parts || [];
    for (let p = parts.length - 1; p >= 0; p--) {
      const part = parts[p];
      if (part.type !== "tool" || !isCreatedFileTool(part.toolName)) continue;
      const argsStr =
        part.input !== undefined && part.input !== null
          ? typeof part.input === "string" ? part.input : JSON.stringify(part.input)
          : part.inputText || null;
      const resultStr =
        part.output !== undefined && part.output !== null
          ? typeof part.output === "string" ? part.output : JSON.stringify(part.output)
          : null;
      const context = buildCreatedFileContext(argsStr, resultStr, part.toolName);
      return {
        key: `${i}:${p}:${context.fileId || context.fileName || "pending"}`,
        ...context,
      };
    }
  }
  return null;
}

export const messageRowSx = {
  px: 2.5,
  py: 0.75,
  transition: "background-color 100ms ease",
  "&:hover": { backgroundColor: "action.hover" },
};

export function getAgentAvatarClickHandler(agentId, onOpenAgentSettings) {
  if (!agentId || typeof onOpenAgentSettings !== "function") return undefined;
  return (event) => {
    event.stopPropagation();
    onOpenAgentSettings(agentId);
  };
}

export function getClickableAgentAvatarSx(avatarClick) {
  if (!avatarClick) return {};
  return {
    cursor: "pointer",
    transition:
      "transform 140ms ease, box-shadow 140ms ease, filter 140ms ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 2px 8px rgba(24, 24, 27, 0.2)",
      filter: "brightness(0.98)",
    },
    "&:active": {
      transform: "translateY(0)",
      boxShadow: "0 1px 4px rgba(24, 24, 27, 0.16)",
    },
  };
}

export function stripInterAgentHeader(text) {
  if (!text) return text;
  return text
    .replace(
      /---\s*INTER-AGENT MESSAGE\s*---[\s\S]*?---\s*END INTER-AGENT MESSAGE HEADER\s*---\s*/g,
      ""
    )
    .trim();
}

/**
 * Resolve the label/avatar name for a user message bubble. Maps the authenticated Cognito
 * username stored on team transcripts to the workspace display name, and falls back to the
 * viewer's profile when agent messages omit sender attribution. The viewer identity
 * ({@code viewerName}/{@code viewerDisplayName}) is supplied by the caller so this stays a pure
 * function with no store dependency.
 */
export function resolveUserSenderDisplayName(
  msg,
  {
    forceUserNameAsYou = false,
    publicUserDisplayName = "",
    viewerName = "",
    viewerDisplayName = "",
  } = {}
) {
  if (forceUserNameAsYou) return "You";
  if (publicUserDisplayName) return publicUserDisplayName;

  const persisted = msg?.senderDisplayName || msg?.senderName || null;

  if (persisted && viewerName && persisted === viewerName && viewerDisplayName) {
    return viewerDisplayName;
  }
  if (!persisted && viewerDisplayName) {
    return viewerDisplayName;
  }
  return persisted || "You";
}

export function normalizeChatUiPolicy(chatUiPolicy) {
  return {
    showSystemPromptInChat: chatUiPolicy?.showSystemPromptInChat === true,
    showToolCallsAndOutputsInChat:
      chatUiPolicy?.showToolCallsAndOutputsInChat !== false,
    showInterAgentMessagesInChat:
      chatUiPolicy?.showInterAgentMessagesInChat !== false,
    showWorkflowProvenanceInChat:
      chatUiPolicy?.showWorkflowProvenanceInChat !== false,
    showSchedulingActivityInChat:
      chatUiPolicy?.showSchedulingActivityInChat !== false,
    showSessionOperationalMetaInChat:
      chatUiPolicy?.showSessionOperationalMetaInChat !== false,
    showSupervisorControlMessagesInChat:
      chatUiPolicy?.showSupervisorControlMessagesInChat !== false,
    enableHumanTakeoverMode: chatUiPolicy?.enableHumanTakeoverMode === true,
    forceUserNameAsYou: chatUiPolicy?.forceUserNameAsYou === true,
    compactToolCalls: chatUiPolicy?.compactToolCalls === true,
    publicUserDisplayName: chatUiPolicy?.publicUserDisplayName || "",
  };
}

export function parseSupervisorDecisionContent(content) {
  if (typeof content !== "string") return null;

  const normalized = content.replace(/\r\n/g, "\n").trim();
  const headerMatch = normalized.match(
    /Supervisor review\s*\(round\s*(\d+)\)\s*:\s*(APPROVE|REVISE|TAKEOVER)\b/i
  );
  if (!headerMatch) return null;

  const round = Number(headerMatch[1]);
  const decision = headerMatch[2].toUpperCase();
  const headerEnd = (headerMatch.index || 0) + headerMatch[0].length;
  const trailing = normalized
    .slice(headerEnd)
    .replace(/^[\s\-:]+/, "")
    .trim();
  let feedback = trailing ? trailing.replace(/^Feedback:\s*/i, "").trim() : "";
  let takeoverResponse = "";
  const actions = [];

  const isActionLine = (line) => {
    const text = (line || "").trim();
    return /^[a-z][a-z0-9_]{1,80}$/i.test(text) && text.includes("_");
  };

  if (feedback) {
    const lines = feedback.split("\n");
    const actionLineIndexes = [];
    lines.forEach((line, idx) => {
      if (isActionLine(line)) {
        actionLineIndexes.push(idx);
        actions.push(line.trim());
      }
    });

    if (decision === "TAKEOVER" && actionLineIndexes.length > 0) {
      const lastActionIndex = actionLineIndexes[actionLineIndexes.length - 1];
      const before = lines.slice(0, lastActionIndex).join("\n").trim();
      const after = lines
        .slice(lastActionIndex + 1)
        .join("\n")
        .trim();
      feedback = before;
      takeoverResponse = after;
    } else if (actionLineIndexes.length > 0) {
      feedback = lines
        .filter((line) => !isActionLine(line))
        .join("\n")
        .trim();
    }
  }

  return {
    round: Number.isFinite(round) ? round : null,
    decision,
    feedback,
    takeoverResponse,
    actions: Array.from(new Set(actions)),
    raw: normalized,
  };
}

export function parseDurationToMs(durationStr) {
  if (!durationStr) return 0;
  let total = 0;
  const pattern = /(\d+)\s*(d|h|m|s)/gi;
  let match;
  while ((match = pattern.exec(durationStr)) !== null) {
    const val = parseInt(match[1], 10);
    switch (match[2].toLowerCase()) {
      case "d":
        total += val * 86400000;
        break;
      case "h":
        total += val * 3600000;
        break;
      case "m":
        total += val * 60000;
        break;
      case "s":
        total += val * 1000;
        break;
    }
  }
  return total;
}

export function buildSessionEmailAddress(inboxAddress, threadToken) {
  if (!inboxAddress || !threadToken) return null;
  const atIdx = inboxAddress.indexOf("@");
  if (atIdx < 0) return null;
  return (
    inboxAddress.substring(0, atIdx) +
    "+" +
    threadToken +
    inboxAddress.substring(atIdx)
  );
}

export function formatShareId(shareId) {
  if (!shareId) return "";
  if (shareId.length <= 16) return shareId;
  return `${shareId.slice(0, 8)}...${shareId.slice(-6)}`;
}

export function parseFormSubmissionAnswers(content) {
  if (typeof content !== "string") return null;
  const lines = content.split("\n");
  const answerLines = lines.filter((l) => l.startsWith("- "));
  if (!answerLines.length) return null;
  const answers = answerLines.map((line) => {
    const text = line.replace(/^- /, "");
    const colonIdx = text.indexOf(": ");
    if (colonIdx === -1) return { label: text, value: "" };
    return { label: text.slice(0, colonIdx), value: text.slice(colonIdx + 2) };
  });
  return { answers };
}

export function formatDateLabel(dateStr) {
  // Java's ZonedDateTime.toString() appends a bracketed zone region (e.g.
  // "...Z[UTC]") that the JS Date constructor cannot parse. Strip it so both
  // pure-ISO chat timestamps and backend session timestamps round-trip.
  const cleaned =
    typeof dateStr === "string" ? dateStr.replace(/\[[^\]]+\]$/, "") : dateStr;
  const date = new Date(cleaned);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (target.getTime() === today.getTime()) return "Today";
  if (target.getTime() === yesterday.getTime()) return "Yesterday";
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

export function getSmartScheduleSuggestion() {
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();

  const target = new Date(now);
  target.setSeconds(0, 0);

  if (day >= 1 && day <= 5 && hour < 9) {
    target.setHours(9, 0);
  } else if (day === 5 && hour >= 9) {
    target.setDate(target.getDate() + (8 - day));
    target.setHours(9, 0);
  } else if (day === 6) {
    target.setDate(target.getDate() + 2);
    target.setHours(9, 0);
  } else if (day === 0) {
    target.setDate(target.getDate() + 1);
    target.setHours(9, 0);
  } else {
    target.setDate(target.getDate() + 1);
    target.setHours(9, 0);
  }

  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const targetDay = target.getDay();
  const isToday =
    target.getDate() === now.getDate() &&
    target.getMonth() === now.getMonth() &&
    target.getFullYear() === now.getFullYear();
  const isTomorrow =
    target.getDate() === now.getDate() + 1 &&
    target.getMonth() === now.getMonth() &&
    target.getFullYear() === now.getFullYear();

  let label;
  if (isToday) label = "Today at 9:00 AM";
  else if (isTomorrow) label = "Tomorrow at 9:00 AM";
  else label = `${dayNames[targetDay]} at 9:00 AM`;

  return { label, date: target };
}

/**
 * Fingerprint of recent message content used to trigger stick-to-bottom scroll.
 * Streaming assistant messages update parts in place (not content/messageCount),
 * so scroll logic must account for parts text and tool/reasoning growth.
 */
export function computeConversationScrollRevision(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return 0;

  let fingerprint = messages.length;
  const tail = messages.slice(-2);

  for (const msg of tail) {
    if (typeof msg?.content === "string") {
      fingerprint += msg.content.length;
    }
    if (Array.isArray(msg?.parts)) {
      fingerprint += msg.parts.length * 512;
      for (const part of msg.parts) {
        if (typeof part.text === "string") fingerprint += part.text.length;
        if (typeof part.state === "string") fingerprint += part.state.length;
        if (part.type === "tool") fingerprint += 64;
      }
    }
    if (msg?.status === "streaming") fingerprint += 1;
  }

  return Math.floor(fingerprint / 40);
}

/** Fingerprint team live-activity cards so stick-to-bottom runs while child steps stream. */
export function computeTeamActivityScrollRevision(teamActivityByRun) {
  if (!teamActivityByRun) return 0;

  let fingerprint = 0;
  for (const entry of Object.values(teamActivityByRun)) {
    if (!entry) continue;
    if (entry.status === "running") fingerprint += 1024;
    fingerprint += computeConversationScrollRevision(entry.messages || []);
    if (Array.isArray(entry.persistedMessages)) {
      fingerprint += computeConversationScrollRevision(entry.persistedMessages);
    }
  }
  return fingerprint;
}
